import { ModelTransformer } from '../../../common/common.model';
import { sortFn, uniqueFilterFn } from '../../_common';
import { ColourPoolItem } from '../../core/definition.scorecard.model';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { ScorecardDto } from '../../scorecard.model';

import {
    RankFilter,
    ScorecardPublishStatusFilter,
    StcScorecardDto,
    StcVerticalBarLineData,
    StcVerticalBarLineDataBarGroup,
    StcVerticalBarLineDataLine,
    SuperGroupFilter,
} from './scorecard-trend-chart.model';
import { ScorecardTrendChart } from './scorecard-trend-chart.state';

export class RanksFilterTransformer
    implements ModelTransformer<RankFilter[], ScorecardDto[]>
{
    constructor(private readonly rankColourPool: ColourPoolItem[]) {}
    transform(model: ScorecardDto[]): RankFilter[] {
        const allRanksData = model.map((x) => x.ranks);
        const flatRanksData = [].concat(...allRanksData);
        const ranksFilterData = flatRanksData
            .filter((value, index, self) =>
                uniqueFilterFn(value, index, self, 'rankTypeName'),
            )
            .sort((a, b) => sortFn(a, b, 'rankTypeName'));

        ranksFilterData.forEach((rank, index) => {
            const rankColour = this.rankColourPool.find(
                (x) => x.useOrder === index + 1,
            );

            rank.rankName = rank.rankTypeName;
            rank.isSelected = true;
            // eslint-disable-next-line no-self-assign
            rank.maxRank = rank.maxRank;
            rank.maxRankRange = Math.max.apply(
                Math,
                flatRanksData.map(function (e) {
                    return e.maxRank;
                }),
            );
            rank.colour = rankColour ? rankColour.colour : '#000';
        });

        return ranksFilterData;
    }
}

export class SuperGroupsFilterTransformer
    implements ModelTransformer<SuperGroupFilter[], ScorecardDto[]>
{
    constructor(private readonly superGroupColourPool: ColourPoolItem[]) {}

    transform(model: ScorecardDto[]): SuperGroupFilter[] {
        const superGroupsFilterData = model
            .map((raw) => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return {
                    id: raw.groupId,
                    name: raw.groupShortName,
                    fullName: raw.groupName,
                    isSelected: true,
                    canBeDeselected: true,
                } as SuperGroupFilter;
            })
            .filter((value, index, self) =>
                uniqueFilterFn(value, index, self, 'id'),
            )
            .sort((a, b) => sortFn(a, b, 'name'));

        if (superGroupsFilterData.length === 1) {
            superGroupsFilterData[0].canBeDeselected = false;
        }

        superGroupsFilterData.forEach((superGroup, index) => {
            const superGroupColour = this.superGroupColourPool.find(
                (x) => x.useOrder === index + 1,
            );
            superGroup.colour = superGroupColour
                ? superGroupColour.colour
                : '#000';
        });
        return superGroupsFilterData;
    }
}

export class ScorecardPublishedStatusFilterTransformer
    implements ModelTransformer<ScorecardPublishStatusFilter[], ScorecardDto[]>
{
    transform(model: ScorecardDto[]): ScorecardPublishStatusFilter[] {
        const scorecardPublishedStatusRawData = model
            .map((x) => {
                return {
                    isPublished: x.published,
                };
            })
            .filter((value, index, self) =>
                uniqueFilterFn(value, index, self, 'isPublished'),
            )
            .sort((a, b) =>
                a.isPublished === b.isPublished ? 0 : a.isPublished ? -1 : 1,
            );

        const filteredScorecardStatuses = scorecardPublishedStatusRawData.map(
            (s) => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return {
                    name: s.isPublished
                        ? TranslationKeyMap._9252_PUBLISHED_SCORECARDS
                        : TranslationKeyMap._9254_UNPUBLISHED_SCORECARDS,
                    isPublished: s.isPublished,
                    isVisible: scorecardPublishedStatusRawData.length > 1,
                    canBeDeselected: scorecardPublishedStatusRawData.length > 1,
                    isSelected: true,
                } as ScorecardPublishStatusFilter;
            },
        );

        return filteredScorecardStatuses;
    }
}

export class VerticalBarLineDataTransformer
    implements ModelTransformer<StcVerticalBarLineData, ScorecardTrendChart>
{
    constructor(
        private readonly data: ScorecardDto[],
        private readonly selectedScorecardId: number,
    ) {}

    transform(model: ScorecardTrendChart): StcVerticalBarLineData {
        return {
            bars: this._getVerticalBarsData(model),
            lines: this._getTrendLinesData(model),
        };
    }

    private _getTrendLinesData(
        model: ScorecardTrendChart,
    ): StcVerticalBarLineDataLine[] {
        const selectedRanksData = model.ranksFilter.filter((x) => x.isSelected);
        const selectedSupergroupsData = model.supergroupsFilter.filter(
            (x) => x.isSelected,
        );
        const rankLinesRawData = this.data
            .filter(
                (x) =>
                    selectedRanksData.some((y) =>
                        x.ranks.some((z) => z.rankTypeId === y.rankTypeId),
                    ) &&
                    selectedSupergroupsData.some((y) => y.id === x.groupId),
            )
            .sort((a, b) => sortFn(a, b, 'endDate'));

        const rankLinesGroupedData: StcVerticalBarLineDataLine[] = [];

        rankLinesRawData.forEach((group) => {
            group.ranks
                .filter((rank) =>
                    selectedRanksData.some(
                        (y) => y.rankTypeId === rank.rankTypeId,
                    ),
                )
                .forEach((rank) => {
                    if (
                        !rankLinesGroupedData.some(
                            (x) => x.id === rank.rankTypeName,
                        )
                    ) {
                        rankLinesGroupedData.push({
                            id: rank.rankTypeName,
                            index: rank.rankTypeId,
                            paths: [],
                            maxRankValue: rank.maxRank,
                        });
                    }

                    const paths = rankLinesGroupedData.find(
                        (x) => x.id === rank.rankTypeName,
                    ).paths;

                    if (!paths.some((x) => x.key === rank.rankTypeName)) {
                        paths.push({ key: rank.rankTypeName, points: [] });
                    }

                    const points = paths.find(
                        (x) => x.key === rank.rankTypeName,
                    ).points;

                    points.push({
                        x: group.endDate,
                        y: rank.myRank,
                    });
                });
        });

        return rankLinesGroupedData.sort((a, b) =>
            sortFn(a, b, 'index', 'desc'),
        );
    }

    private _getVerticalBarsData(
        model: ScorecardTrendChart,
    ): StcVerticalBarLineDataBarGroup[] {
        const scorecardTrendRawData =
            model.supergroupsFilter &&
            model.supergroupsFilter.length > 0 &&
            this.data
                .map((raw) => {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    return {
                        scorecardId: raw.scorecardId,
                        userId: raw.userId,
                        groupId: raw.groupId,
                        groupShortName: raw.groupShortName,
                        startDate: raw.startDate,
                        endDate: raw.endDate,
                        adjustedPoints: raw.adjustedPoints,
                        published: raw.published,
                        visible: this._isVisible(model, raw),
                    } as StcScorecardDto;
                })
                .filter((value, index, self) =>
                    uniqueFilterFn(value, index, self, 'scorecardId'),
                )
                .filter((value) => value.visible)
                .sort((a, b) => sortFn(a, b, 'groupShortName'));

        const scorecardTrendGroupedData: StcVerticalBarLineDataBarGroup[] = [];

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        scorecardTrendRawData &&
            scorecardTrendRawData.forEach((element) => {
                // Setting first day of 'end' month as key as the endDate may be different in each scorecard under the same period
                const groupKey = new Date(
                    element.endDate.getFullYear(),
                    element.endDate.getMonth(),
                    1,
                );

                if (
                    !scorecardTrendGroupedData.some(
                        (x) => x.key.getTime() === groupKey.getTime(),
                    )
                ) {
                    scorecardTrendGroupedData.push({
                        key: groupKey,
                        startDate: element.startDate,
                        endDate: element.endDate,
                        values: [],
                    });
                }

                const group = scorecardTrendGroupedData.find(
                    (x) => x.key.getTime() === groupKey.getTime(),
                );

                group.values.push({
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    id: `${element.groupId}-${element.published}`,
                    groupId: element.groupId,
                    name: element.groupShortName,
                    value: {
                        scorecardId: element.scorecardId,
                        y: element.adjustedPoints,
                    },
                    isSelected:
                        element.scorecardId === this.selectedScorecardId,
                    isPublished: element.published,
                });
            });

        const isAnyBarSelected = scorecardTrendGroupedData.some((g) =>
            g.values.some((b) => b.isSelected),
        );
        if (!isAnyBarSelected) {
            const lastBarsGroup =
                scorecardTrendGroupedData[scorecardTrendGroupedData.length - 1];
            const lastVisibleBar =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                lastBarsGroup &&
                lastBarsGroup.values.find((x) => x.isPublished) &&
                lastBarsGroup.values[lastBarsGroup.values.length - 1];
            if (lastVisibleBar) {
                lastVisibleBar.isSelected = true;
            }
        }
        return scorecardTrendGroupedData.sort((a, b) => sortFn(a, b, 'key'));
    }

    private _isVisible(model: ScorecardTrendChart, raw: ScorecardDto): boolean {
        const rawSuperGroupFilter = model.supergroupsFilter.find(
            (x) => x.id === raw.groupId,
        );
        const rawPublishStatusFilter = model.publishStatusFilter.find(
            (x) => x.isPublished === raw.published,
        );

        if (rawSuperGroupFilter && rawPublishStatusFilter) {
            return (
                rawSuperGroupFilter.isSelected &&
                rawPublishStatusFilter.isSelected
            );
        } else {
            return false;
        }
    }
}
